import { CounterClockwiseClockIcon } from "@radix-ui/react-icons";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { metadataForRestore } from "@/components/canvas-editor/snapshot-consistency";
import {
  FeedPageHeader,
  FeedPageIcon,
  FeedPageTitle,
} from "@/components/FeedPageHeader";
import { HistoryItem } from "@/components/HistoryItem";
import { Page } from "@/components/Page";
import { PublishBadge } from "@/components/PublishBadge";
import { ButtonLink } from "@/components/ui/button";
import { editFeed, feedHistoryQueryOptions } from "@/lib/api";
import { Feed, FeedVersion } from "@/lib/types";
import { useFeedAccess } from "@/lib/useFeedAccess";

export const Route = createFileRoute(
  "/app/_sidebar-layout/feed/$feedId/version-history",
)({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { feedId } }) => {
    const feed = await queryClient.ensureQueryData(
      feedHistoryQueryOptions(parseInt(feedId)),
    );

    return {
      title: feed.display_name,
    };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData?.title }],
  }),
});

function FeedHistoryItem({
  feedVersion,
  feed,
}: {
  feedVersion: FeedVersion;
  feed: Feed;
}) {
  const navigate = useNavigate({ from: "/app/feed/$feedId/version-history" });
  const historyAccess = useFeedAccess(feed);
  const onRestore = useMutation({
    mutationFn: async () => {
      const form = new FormData();

      form.set("record_name", feed.record_name);
      form.set("algorithm_manifest", JSON.stringify(feedVersion.manifest));

      // Restore the canvas this manifest was saved with, not the one the feed
      // happens to be on now: a restore that sends only the manifest leaves the
      // newer canvas standing in front of older rules. Sending no metadata is
      // still right when the version has none -- the canvas then rebuilds from
      // the manifest, which at least agrees with it.
      //
      // This path never renders FeedForm, so the save guard there does not run
      // on it. It has to make the same check itself, and it has no mounted
      // editor to make it with: metadataForRestore() reads the stored snapshot
      // directly and drops one that no longer describes the manifest it is
      // stored beside. Rows written before that guard landed hold such pairs.
      const metadata = metadataForRestore(feedVersion);

      if (metadata) {
        form.set("metadata", JSON.stringify(metadata));
      }

      await editFeed(feed.id, form);
      await navigate({
        to: "/app/feed/$feedId/view",
        params: { feedId: `${feed.id}` },
      });
    },
    onSuccess: () =>
      navigate({
        to: "/app/feed/$feedId/view",
        params: { feedId: `${feed.id}` },
      }),
  });

  return (
    <HistoryItem
      feedVersion={feedVersion}
      mutation={onRestore}
      // Reading the history is useful to a collaborator; restoring is not theirs to do.
      canRestore={historyAccess.isOwner}
    />
  );
}

function RouteComponent() {
  const { feedId } = Route.useParams();
  const { data: feed } = useSuspenseQuery(
    feedHistoryQueryOptions(parseInt(feedId)),
  );
  const manifests = useMemo(
    () => [...feed.algorithm_manifests].reverse(),
    [feed.algorithm_manifests],
  );

  return (
    <Page>
      <FeedPageHeader>
        <PublishBadge feed={feed} />
        <FeedPageTitle
          title={feed.display_name}
          icon={
            <FeedPageIcon>
              <CounterClockwiseClockIcon />
            </FeedPageIcon>
          }
        >
          <ButtonLink
            to="/app/feed/$feedId/view"
            params={{ feedId: `${feed.id}` }}
          >
            Exit Version History
          </ButtonLink>
        </FeedPageTitle>
      </FeedPageHeader>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {manifests.map((feedVersion) => (
          <FeedHistoryItem
            feedVersion={feedVersion}
            feed={feed}
            key={feed.created_at}
          />
        ))}
      </div>
    </Page>
  );
}
